import { createAsyncThunk } from "@reduxjs/toolkit";
import { showSnackbar } from "../slices/SnackbarSlice";
import {
  adminLoginApi,
  forgotPasswordApi,
  getCurrentUserApi,
  loginApi,
  registerApi,
  resetEmployeePasswordApi,
  resetPasswordApi,
  acceptInviteApi,
} from "../apis";
import {
  IAuthData,
  IForgotPasswordData,
  IResetPasswordData,
} from "./../apis/IApisInterface";
import {
  bridgeExchangeTokenApi,
  exchangeGBPFetchTokenApi,
  exchangeGBPTokenApi,
  exchangeReconnectGBPTokenApi,
  reconnectGBPPlatformApi,
  adminUserBypassApi,
  authAppleExchangeTokenApi,
} from "../apis/AuthApi";
import { getFirstAvailableRoute } from "../../utils/moduleNavigation";
import { routes } from "../../utils/routes";
import { getOnboardingStateAction } from "./UserActions/OnboardingActions";

const AUTH_LOGIN = "auth/login";
const AUTH_REGISTER = "auth/register";
const AUTH_ADMIN_LOGIN = "auth/adminlogin";
const AUTH_CURRENT = "auth/current";
const AUTH_FORGOT_PASSWORD = "/auth/forgot-password";
const AUTH_RESET_PASSWORD = "/auth/reset-password";
const AUTH_RESET_PASSWORD_EMPLOYEE = "/auth/reset-password-employee";

interface IAuthPayload {
  data: IAuthData;
  navigate?: any;
  redirectTo?: string;
  inviteToken?: string;
}

interface IForgotPasswordPayload {
  data: IForgotPasswordData;
}

interface IResetPasswordPayload {
  data: IResetPasswordData;
  navigate: any;
}

export const loginAction = createAsyncThunk(
  AUTH_LOGIN,
  async (
    { data, navigate, redirectTo, inviteToken }: IAuthPayload,
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await loginApi(data);
      dispatch(
        showSnackbar({ message: response?.data?.message, type: "success" }),
      );
      let modules = response?.data?.data?.user?.modules;
      const userToken = response?.data?.data?.token;

      if (userToken) {
        localStorage.setItem("token_user", userToken);
      }

      if (!redirectTo && userToken) {
        try {
          const onboardingRes = await dispatch(
            getOnboardingStateAction() as any,
          ).unwrap();
          if (onboardingRes?.data && onboardingRes.data.completed === false) {
            if (navigate) navigate(routes.onboarding);
            return response.data;
          }
        } catch (onboardingErr) {
          console.error(
            "Failed to fetch onboarding state after login:",
            onboardingErr,
          );
        }
      }

      if (navigate) {
        navigate(redirectTo || getFirstAvailableRoute(modules));
      }
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message || "Login failed",
          type: "error",
        }),
      );
      return rejectWithValue(error || { message: "Login failed" });
    }
  },
);

export const adminLoginAction = createAsyncThunk(
  AUTH_ADMIN_LOGIN,
  async ({ data, navigate }: IAuthPayload, { rejectWithValue, dispatch }) => {
    try {
      const response = await adminLoginApi(data);
      dispatch(
        showSnackbar({
          message: response?.data?.message || "Login failed",
          type: "success",
        }),
      );
      if (navigate) {
        navigate("/admin/dashboard");
      }
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
      return rejectWithValue(error || { message: "Login failed" });
    }
  },
);

export const registerAction = createAsyncThunk(
  AUTH_REGISTER,
  async (
    { data, navigate, redirectTo, inviteToken }: IAuthPayload,
    { rejectWithValue, dispatch },
  ) => {
    try {
      const payload: IAuthData = inviteToken ? { ...data, inviteToken } : data;
      const response = await registerApi(payload);
      dispatch(
        showSnackbar({ message: response?.data?.message, type: "success" }),
      );
      let modules = response?.data?.data?.user?.modules;
      const userToken = response?.data?.data?.token;

      if (userToken) {
        localStorage.setItem("token_user", userToken);
      }

      if (inviteToken && userToken) {
        try {
          // Fetch the updated user profile now that the workspace has been joined
          const domain = window.location.origin.split("//")[1];
          const profileRes = await getCurrentUserApi(domain);

          if (profileRes?.data?.data) {
            const updatedUser = profileRes.data.data;

            // Merge active workspace, workspaces list, and profile into registration response
            response.data.data.user = updatedUser;
            response.data.data.activeWorkspaceId =
              updatedUser.activeWorkspaceId || updatedUser.agency?._id || null;
            response.data.data.workspaces = updatedUser.workspaces || [];
            modules = updatedUser.modules;
          }
        } catch (err) {
          console.error(
            "Failed to fetch user profile after registration:",
            err,
          );
        }
      }

      if (!redirectTo && userToken) {
        try {
          const onboardingRes = await dispatch(
            getOnboardingStateAction() as any,
          ).unwrap();
          if (onboardingRes?.data && onboardingRes.data.completed === false) {
            if (navigate) navigate(routes.onboarding);
            return response.data;
          }
        } catch (onboardingErr) {
          console.error(
            "Failed to fetch onboarding state after registration:",
            onboardingErr,
          );
        }
      }

      if (navigate) navigate(redirectTo || getFirstAvailableRoute(modules));
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
      return rejectWithValue(error || { message: "Register failed" });
    }
  },
);

export const forgotPasswordAction = createAsyncThunk(
  AUTH_FORGOT_PASSWORD,
  async ({ data }: IForgotPasswordPayload, { rejectWithValue, dispatch }) => {
    try {
      const response = await forgotPasswordApi(data);
      dispatch(
        showSnackbar({ message: response?.data?.message, type: "success" }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
      return rejectWithValue(error || { message: "Forgot password failed" });
    }
  },
);

export const resetPasswordAction = createAsyncThunk(
  AUTH_RESET_PASSWORD,
  async (
    { data, navigate }: IResetPasswordPayload,
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await resetPasswordApi(data);
      dispatch(
        showSnackbar({ message: response?.data?.message, type: "success" }),
      );
      navigate("/login");
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
      return rejectWithValue(error || { message: "Reset password failed" });
    }
  },
);
export const resetEmployeePasswordAction = createAsyncThunk(
  AUTH_RESET_PASSWORD_EMPLOYEE,
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await resetEmployeePasswordApi(data);
      dispatch(
        showSnackbar({ message: response?.data?.message, type: "success" }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
      return rejectWithValue(error || { message: "Reset password failed" });
    }
  },
);

export const getCurrentUserAction = createAsyncThunk(
  AUTH_CURRENT,
  async (
    payload: string | { domain: string; skipAuth?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const domain = typeof payload === "string" ? payload : payload.domain;
      const skipAuth = typeof payload === "string" ? false : !!payload.skipAuth;
      const response = await getCurrentUserApi(domain, skipAuth);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        ...(error?.response?.data || { message: "Could not get profile" }),
        status: error?.response?.status ?? null,
      });
    }
  },
);
export const bridgeExchangeTokenAction = createAsyncThunk(
  "bridgetoken/exchange",
  async (
    {
      bridge_token,
      workspaceId,
    }: { bridge_token: string; workspaceId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await bridgeExchangeTokenApi(bridge_token, workspaceId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Could not get profile" },
      );
    }
  },
);

export const authAppleExchangeTokenAction = createAsyncThunk(
  "auth-apple/exchange",
  async ({ bridge_token }: { bridge_token: string }, { rejectWithValue }) => {
    try {
      const response = await authAppleExchangeTokenApi(bridge_token);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Could not get profile" },
      );
    }
  },
);

export const exchangeGBPTokenAction = createAsyncThunk(
  "GBP/exchange",
  async (
    {
      bridge_token,
      token_data,
      workspaceId,
    }: { bridge_token: string; token_data: string; workspaceId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await exchangeGBPTokenApi(
        bridge_token,
        token_data,
        workspaceId,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Could not get profile" },
      );
    }
  },
);
export const exchangeGBPFetchTokenAction = createAsyncThunk(
  "bridgetoken/exchange",
  async (
    {
      bridge_token,
      code,
      workspaceId,
    }: { bridge_token: string; code: string; workspaceId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await exchangeGBPFetchTokenApi(
        bridge_token,
        code,
        workspaceId,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Could not get profile" },
      );
    }
  },
);

export const exchangeReconnectGBPTokenAction = createAsyncThunk(
  "bridgetoken/exchange-reconnect",
  async (
    {
      bridge_token,
      code,
      workspaceId,
    }: { bridge_token: string; code: string; workspaceId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await exchangeReconnectGBPTokenApi(
        bridge_token,
        code,
        workspaceId,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Could not get profile" },
      );
    }
  },
);

export const reconnectGBPPlatformAction = createAsyncThunk(
  "gbp/reconnect",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await reconnectGBPPlatformApi(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Could not get profile" },
      );
    }
  },
);

export const bypassUserLoginAction = createAsyncThunk(
  "admin/bypass-user-login",
  async ({ userId, workspaceId }: { userId: string; workspaceId?: string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await adminUserBypassApi(userId, workspaceId);
      const userToken = response?.data?.data?.token;
      if (userToken) {
        localStorage.setItem("token_user", userToken);
      }
      dispatch(
        showSnackbar({
          message: response?.data?.message || "Bypass login successful",
          type: "success",
        }),
      );
      const modules = response?.data?.data?.user?.modules;
      window.open(getFirstAvailableRoute(modules), "_blank");
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message || "Bypass login failed",
          type: "error",
        }),
      );
      return rejectWithValue(error || { message: "Bypass login failed" });
    }
  },
);
